import { database } from "..";
import Appointment from "../model/appointment";
import Photo from "../model/photo";
import Proposal from "../model/proposal";
import Customer from "../model/customer";
import Email from "../model/email";
import SmsMessage from "../model/smsMessage";
import { formatMoney } from "../utils";
import { APIProposalStatus } from "./proposals";

export type ActivityPhoto = {
  type: "photo";
  id: string;
  photoData: string;
  createdAt: number;
};

export type ActivityProposal = {
  type: "proposal";
  id: string;
  createdAt: number;
  totalAmount: number;
  status: string;
  archived?: boolean;
};

export type ActivityAppointment = {
  type: "appointment";
  id: string;
  createdAt: number;
  startTime: number;
  endTime: number;
  notes: string;
};

export type APIActivity = ActivityPhoto | ActivityProposal | ActivityAppointment;

// One entry in the activity feed, whatever kind of record it came from.
export type FeedItem = {
  kind: "proposal" | "appointment" | "photo" | "email" | "sms";
  id: string;
  when: number;
  customerId: string;
  customerName: string;
  title: string;
  detail: string;
  link: string;
  photoData?: string;
  status?: string;
};

export async function getActivityFeed(): Promise<FeedItem[]> {
  const customers = await database.get<Customer>("customers").query().fetch();
  const names = new Map(
    customers.map((c) => [
      c.id,
      `${c.firstName || ""} ${c.lastName || ""}`.trim() || "Unnamed customer",
    ])
  );
  const proposals = await database.get<Proposal>("proposals").query().fetch();
  const proposalCustomer = new Map(
    proposals.map((p) => [p.id, p.customer?.id || ""])
  );
  const nameOf = (id: string) => names.get(id) || "";

  const items: FeedItem[] = [];
  for (const p of proposals) {
    const cid = p.customer?.id || "";
    items.push({
      kind: "proposal",
      id: p.id,
      when: p.createdAt || 0,
      customerId: cid,
      customerName: nameOf(cid) || `${p.firstName || ""} ${p.lastName || ""}`.trim(),
      title: `Proposal · ${formatMoney(p.totalAmount || 0)}`,
      detail: [p.serviceAddressLine1, p.serviceCity].filter(Boolean).join(", "),
      link: `/proposals?id=${p.id}`,
      status: p.status,
    });
  }
  for (const a of await database
    .get<Appointment>("appointments")
    .query()
    .fetch()) {
    const cid = a.customer?.id || "";
    const start = a.startTime || a.createdAt || 0;
    items.push({
      kind: "appointment",
      id: a.id,
      when: start,
      customerId: cid,
      customerName: nameOf(cid),
      title: "Sales call",
      detail: a.notes || "",
      link: `/appointments?id=${a.id}`,
    });
  }
  for (const ph of await database.get<Photo>("photos").query().fetch()) {
    const cid = ph.customer?.id || "";
    items.push({
      kind: "photo",
      id: ph.id,
      when: ph.createdAt || 0,
      customerId: cid,
      customerName: nameOf(cid),
      title: "Photo",
      detail: "",
      link: `/photos/details?id=${ph.id}&cid=${cid}`,
      photoData: ph.photoData,
    });
  }
  for (const e of await database.get<Email>("emails").query().fetch()) {
    const cid =
      e.refType === "customer"
        ? e.refId || ""
        : proposalCustomer.get(e.refId || "") || "";
    items.push({
      kind: "email",
      id: e.id,
      when: Number(e.generatedAt) || Number(e.createdAt) || 0,
      customerId: cid,
      customerName: nameOf(cid) || e.to || "",
      title: `Email · ${e.subject || "(no subject)"}`,
      detail: Number(e.sentAt) ? "Sent" : "Pending",
      link: `/emails?id=${e.id}`,
    });
  }
  for (const m of await database.get<SmsMessage>("sms_messages").query().fetch()) {
    const cid = m.customer?.id || "";
    items.push({
      kind: "sms",
      id: m.id,
      when: Number(m.createdAt) || 0,
      customerId: cid,
      customerName: nameOf(cid) || m.fromNumber || "",
      title: m.direction === "in" ? "Text received" : "Text sent",
      detail: m.body || "",
      link: cid ? `/customers?id=${cid}#texts` : "/activity",
      status: undefined,
    });
  }
  return items.sort((a, b) => b.when - a.when);
}
